/**
 * Responsibility: render the vocabulary from core/models/enums as a chip.
 *
 * One place decides what colour "BLOCKED" is, so a blocked fetch never reads
 * like an out-of-stock one anywhere in the UI. Section 6.6 depends on that
 * distinction being visible rather than collapsed.
 *
 * Deliberately an app component rather than a kit one. It encodes this
 * product's enums, and the kit's rule is that it knows nothing about any
 * specific product - the chip itself is styled by the kit theme, which is
 * where the shared part lives.
 */
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

type Tone = ChipProps['color'];

const AVAILABILITY: Record<string, { label: string; color: Tone }> = {
  IN_STOCK: { label: 'In stock', color: 'success' },
  OUT_OF_STOCK: { label: 'Out of stock', color: 'default' },
  UNKNOWN: { label: 'Unknown', color: 'secondary' },
};

const SELLER: Record<string, { label: string; color: Tone }> = {
  FIRST_PARTY: { label: 'First party', color: 'success' },
  MARKETPLACE: { label: 'Marketplace', color: 'warning' },
  UNKNOWN: { label: 'Seller unknown', color: 'secondary' },
};

const STATUS: Record<string, { label: string; color: Tone }> = {
  OK: { label: 'OK', color: 'success' },
  ERROR: { label: 'Error', color: 'error' },
  BLOCKED: { label: 'Blocked', color: 'error' },
};

const HEALTH: Record<string, { label: string; color: Tone }> = {
  OK: { label: 'OK', color: 'success' },
  SUSPECT: { label: 'Suspect', color: 'warning' },
};

const MAPS = { availability: AVAILABILITY, seller: SELLER, status: STATUS, health: HEALTH };

type StatusChipProps = {
  kind: keyof typeof MAPS;
  value: string;
  size?: ChipProps['size'];
};

export function StatusChip({ kind, value, size = 'small' }: StatusChipProps) {
  const entry = MAPS[kind][value] ?? { label: value, color: 'default' as Tone };
  return <Chip label={entry.label} color={entry.color} size={size} variant="outlined" />;
}
